import axios from "axios";
import { create } from "zustand";
import { LOG } from "../utils/logUtil";
import { useAlert } from "./useAlert";
import { useAppLoading } from "./useAppLoading";
import { IMarketPricing } from "./useMarketPricing";

export enum ConceptType {
  "Full Service" = "fs",
  "Self Service" = "ss",
  "Container" = "c",
  "Post" = "post",
}

export enum HouseholdType {
  Single = "single",
  Couple = "couple",
  Family = "family",
  Other = "other",
}
export interface IOpeningHoursDay {
  /** 0 = Monday ... 6 = Sunday */
  day: number;
  closed?: boolean;
  open?: string;
  close?: string;
  /** Bilingual exception/holiday note, shown per the app's language. */
  note?: { en?: string; nl?: string };
}

/** One-off override for a date or a run of dates (holiday, stocktaking, ...).
 *  A single day is startDate === endDate. */
export interface IOpeningHoursException {
  /** ISO day, "2026-12-25". */
  startDate: string;
  endDate: string;
  closed?: boolean;
  open?: string;
  close?: string;
  note?: { en?: string; nl?: string };
}

export interface IDropLocation {
  _id?: string;
  name?: string;
  cost_place?: string;
  address?: string;
  city?: string;
  area?: string;
  type?: string;
  postalCode?: string;
  lat?: string;
  long?: string;
  image?: string;
  openingHours?: IOpeningHoursDay[];
  openingHoursExceptions?: IOpeningHoursException[];
  openingHoursNote?: { en?: string; nl?: string };
  weeklyOpeningHours?: string;
  weekendOpeningHours?: string;
  description?: string;
  accepting?: Array<{ name: string; icon?: string }>;
  notAccepting?: Array<{ name: string; icon?: string }>;
  conceptType?: ConceptType;
  openingDate?: Date;
  highBuildingsRatio?: number;
  inhabitantsPerHousehold?: number;
  averageIncomeInNeighbourhood?: number;
  householdsType?: HouseholdType;
  catchmentArea?: number;
  householdsInCatchmentArea?: number;
  usersPercentageInCatchmentArea?: number;
  rentPerYear?: number;
  squareMetersPerStore?: number;
  lockerSpaceUsed?: number;
  isPost?: boolean;
  showInApp?: boolean;
  /**
   * Whether this store accepts parcel handoffs. Independent of showInApp — a
   * store hidden from the customer map can still be a collection point.
   */
  parcelHandoffEnabled?: boolean;
  hasBrinksMachines?: boolean;
  createdAt?: Date;
}

export interface ILocationStats {
  _id: string;
  location: IDropLocation;
  //Filters
  filterType:
    | "ALL"
    | "YEARLY"
    | "QUARTERLY"
    | "MONTHLY"
    | "WEEKLY"
    | "WEEKS_AFTER_OPENING";
  year: number;
  month: number;
  startWeekYear: number;
  startWeekMonth: number;
  startWeekDay: number;
  endWeekYear: number;
  endWeekMonth: number;
  endWeekDay: number;
  quarter: "Q1" | "Q2" | "Q3" | "Q4";
  weeksAfterOpening: number;
  // User stats
  allUsers: number;
  oneTimeUsers: number;
  activeUsers: number;
  regularUsers: number;
  inactiveUsers: number;
  // Material stats
  totalMaterialDrops: number;
  totalMaterialRevenue: number;
  totalMaterialVolume: number;
  totalMaterialPayout: number;
  materialsVolume: {
    material: string | IMarketPricing;
    volume: number;
  }[];
  // Dropbot stats
  totalDropbotDrops: number;
  totalDropbotRevenue: number;
  totalDropbotVolume: number;
  totalDropbotPayout: number;
  // Statie stats
  totalStatieDrops: number;
  totalStatieRevenue: number;
  totalStatieItems: number;
  totalStatieDropsByPros: number;
  // Parcels stats
  totalParcelsDrops: number;
  totalParcelsRevenue: number;
  totalParcelsVolume: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface ILocationMaterial {
  name: string;
  data: [string, number][];
}

interface IStore {
  /** State */
  allTimeLocationStats: ILocationStats[];
  yearlyLocationStats: ILocationStats[];
  quarterlyLocationStats: ILocationStats[];
  monthlyLocationStats: ILocationStats[];
  weeklyLocationStats: ILocationStats[];
  weeksAfterOpeningLocationStats: ILocationStats[];
  oneDropLocation: IDropLocation | null;
  isUpdatingParcelHandoffEnabled: boolean;
  parcelHandoffEnabledError: string | null;
  dropLocations: IDropLocation[] | null;
  locationMaterials: ILocationMaterial[] | null;
  isLocationMaterialsLoading?: boolean;
  addedDropLocation?: string | null;
  updatedDropLocation?: string | null;
  /** Callbacks */
  getLocationStats: () => void;
  recalculateAllTimeLocationStats: () => void;
  recalculateYearlyLocationStats: (year: number) => void;
  recalculateQuarterlyLocationStats: (
    year: number,
    quarter: "Q1" | "Q2" | "Q3" | "Q4"
  ) => void;
  recalculateMonthlyLocationStats: (year: number, month: number) => void;
  recalculateWeeklyLocationStats: (startWeek: Date, endWeek: Date) => void;
  recalculateWeeksAfterOpeningLocationStats: (
    weeksAfterOpening: number
  ) => void;
  getDropLocations: () => void;
  getLocationMaterials: (params?: {
    locations?: string[];
    type?: "location" | "city";
    places?: string[];
    period: "week" | "month" | "quarter" | "year";
    dateValue?: string | number;
    year?: number;
    fromYear?: number;
    toYear?: number;
    fromMonth?: number;
    toMonth?: number;
    fromQuarter?: number;
    toQuarter?: number;
    fromWeek?: number;
    toWeek?: number;
  }) => void;
  getOneDropLocation: (id: string) => void;
  addDropLocation: (dropLocation: IDropLocation) => void;
  updateDropLocation: (dropLocation: IDropLocation) => void;
  setParcelHandoffEnabled: (
    locationId: string,
    enabled: boolean
  ) => Promise<boolean>;
  deleteDropLocation: (id: string) => void;
  clearAddedDropLocation: () => void;
  clearUpdatedDropLocation: () => void;
}

export const useDropLocations = create<IStore>()((set, get) => ({
  /** State */
  allTimeLocationStats: [],
  yearlyLocationStats: [],
  quarterlyLocationStats: [],
  monthlyLocationStats: [],
  weeklyLocationStats: [],
  weeksAfterOpeningLocationStats: [],
  oneDropLocation: null,
  dropLocations: null,
  locationMaterials: null,
  isLocationMaterialsLoading: false,
  addedDropLocation: null,
  updatedDropLocation: null,
  isUpdatingParcelHandoffEnabled: false,
  parcelHandoffEnabledError: null,
  /** Callbacks */
  getLocationStats: async () => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get("locations/stats");
      if (response?.data) {
        const allTime =
          response?.data?.filter(
            (locationStat: ILocationStats) => locationStat?.filterType === "ALL"
          ) ?? [];
        const yearly =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "YEARLY"
          ) ?? [];
        const quarterly =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "QUARTERLY"
          ) ?? [];
        const monthly =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "MONTHLY"
          ) ?? [];
        const weeksAfterOpeningData =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "WEEKS_AFTER_OPENING"
          ) ?? [];
        const weekly =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "WEEKLY"
          ) ?? [];
        set({
          allTimeLocationStats: allTime,
          yearlyLocationStats: yearly,
          quarterlyLocationStats: quarterly,
          monthlyLocationStats: monthly,
          weeklyLocationStats: weekly,
          weeksAfterOpeningLocationStats: weeksAfterOpeningData,
        });
      }
    } catch (error) {
      LOG.error("Error on fetching locations stats", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  recalculateAllTimeLocationStats: async () => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get("locations/stats/recalculate");
      if (response?.data) {
        const allTime =
          response?.data?.filter(
            (locationStat: ILocationStats) => locationStat?.filterType === "ALL"
          ) ?? [];
        const yearly =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "YEARLY"
          ) ?? [];
        const quarterly =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "QUARTERLY"
          ) ?? [];
        const monthly =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "MONTHLY"
          ) ?? [];
        const weeksAfterOpeningData =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "WEEKS_AFTER_OPENING"
          ) ?? [];
        const weekly =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "WEEKLY"
          ) ?? [];
        set({
          allTimeLocationStats: allTime,
          yearlyLocationStats: yearly,
          quarterlyLocationStats: quarterly,
          monthlyLocationStats: monthly,
          weeklyLocationStats: weekly,
          weeksAfterOpeningLocationStats: weeksAfterOpeningData,
        });
      }
    } catch (error) {
      LOG.error("Error on recalculating locations stats", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  recalculateYearlyLocationStats: async (year) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post("locations/stats/recalculate/yearly", {
        year: Number(year),
      });
      if (response?.data) {
        const allTime =
          response?.data?.filter(
            (locationStat: ILocationStats) => locationStat?.filterType === "ALL"
          ) ?? [];
        const yearly =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "YEARLY"
          ) ?? [];
        const quarterly =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "QUARTERLY"
          ) ?? [];
        const monthly =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "MONTHLY"
          ) ?? [];
        const weeksAfterOpeningData =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "WEEKS_AFTER_OPENING"
          ) ?? [];
        const weekly =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "WEEKLY"
          ) ?? [];
        set({
          allTimeLocationStats: allTime,
          yearlyLocationStats: yearly,
          quarterlyLocationStats: quarterly,
          monthlyLocationStats: monthly,
          weeklyLocationStats: weekly,
          weeksAfterOpeningLocationStats: weeksAfterOpeningData,
        });
      }
    } catch (error) {
      LOG.error("Error on recalculating locations stats", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  recalculateQuarterlyLocationStats: async (year, quarter) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post(
        "locations/stats/recalculate/quarterly",
        {
          year: Number(year),
          quarter,
        }
      );
      if (response?.data) {
        const allTime =
          response?.data?.filter(
            (locationStat: ILocationStats) => locationStat?.filterType === "ALL"
          ) ?? [];
        const yearly =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "YEARLY"
          ) ?? [];
        const quarterly =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "QUARTERLY"
          ) ?? [];
        const monthly =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "MONTHLY"
          ) ?? [];
        const weeksAfterOpeningData =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "WEEKS_AFTER_OPENING"
          ) ?? [];
        const weekly =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "WEEKLY"
          ) ?? [];
        set({
          allTimeLocationStats: allTime,
          yearlyLocationStats: yearly,
          quarterlyLocationStats: quarterly,
          monthlyLocationStats: monthly,
          weeklyLocationStats: weekly,
          weeksAfterOpeningLocationStats: weeksAfterOpeningData,
        });
      }
    } catch (error) {
      LOG.error("Error on recalculating locations stats", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  recalculateMonthlyLocationStats: async (year, month) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post("locations/stats/recalculate/monthly", {
        year: Number(year),
        month: Number(month),
      });
      if (response?.data) {
        const allTime =
          response?.data?.filter(
            (locationStat: ILocationStats) => locationStat?.filterType === "ALL"
          ) ?? [];
        const yearly =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "YEARLY"
          ) ?? [];
        const quarterly =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "QUARTERLY"
          ) ?? [];
        const monthly =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "MONTHLY"
          ) ?? [];
        const weeksAfterOpeningData =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "WEEKS_AFTER_OPENING"
          ) ?? [];
        const weekly =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "WEEKLY"
          ) ?? [];
        set({
          allTimeLocationStats: allTime,
          yearlyLocationStats: yearly,
          quarterlyLocationStats: quarterly,
          monthlyLocationStats: monthly,
          weeklyLocationStats: weekly,
          weeksAfterOpeningLocationStats: weeksAfterOpeningData,
        });
      }
    } catch (error) {
      LOG.error("Error on recalculating locations stats", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  recalculateWeeklyLocationStats: async (startWeek, endWeek) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const startYear = startWeek.getFullYear();
      const startMonth = startWeek.getMonth() + 1;
      const startDay = startWeek.getDate();
      const endYear = endWeek.getFullYear();
      const endMonth = endWeek.getMonth() + 1;
      const endDay = endWeek.getDate();
      const response = await axios.post("locations/stats/recalculate/weekly", {
        startYear: Number(startYear),
        startMonth: Number(startMonth),
        startDay: Number(startDay),
        endYear: Number(endYear),
        endMonth: Number(endMonth),
        endDay: Number(endDay),
      });
      if (response?.data) {
        const allTime =
          response?.data?.filter(
            (locationStat: ILocationStats) => locationStat?.filterType === "ALL"
          ) ?? [];
        const yearly =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "YEARLY"
          ) ?? [];
        const quarterly =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "QUARTERLY"
          ) ?? [];
        const monthly =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "MONTHLY"
          ) ?? [];
        const weeksAfterOpeningData =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "WEEKS_AFTER_OPENING"
          ) ?? [];
        const weekly =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "WEEKLY"
          ) ?? [];
        set({
          allTimeLocationStats: allTime,
          yearlyLocationStats: yearly,
          quarterlyLocationStats: quarterly,
          monthlyLocationStats: monthly,
          weeklyLocationStats: weekly,
          weeksAfterOpeningLocationStats: weeksAfterOpeningData,
        });
      }
    } catch (error) {
      LOG.error("Error on recalculating locations stats", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  recalculateWeeksAfterOpeningLocationStats: async (weeksAfterOpening) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post(
        "locations/stats/recalculate/weeksAfterOpening",
        {
          weeksAfterOpening,
        }
      );
      if (response?.data) {
        const allTime =
          response?.data?.filter(
            (locationStat: ILocationStats) => locationStat?.filterType === "ALL"
          ) ?? [];
        const yearly =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "YEARLY"
          ) ?? [];
        const quarterly =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "QUARTERLY"
          ) ?? [];
        const monthly =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "MONTHLY"
          ) ?? [];
        const weeksAfterOpeningData =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "WEEKS_AFTER_OPENING"
          ) ?? [];
        const weekly =
          response?.data?.filter(
            (locationStat: ILocationStats) =>
              locationStat?.filterType === "WEEKLY"
          ) ?? [];
        set({
          allTimeLocationStats: allTime,
          yearlyLocationStats: yearly,
          quarterlyLocationStats: quarterly,
          monthlyLocationStats: monthly,
          weeklyLocationStats: weekly,
          weeksAfterOpeningLocationStats: weeksAfterOpeningData,
        });
      }
    } catch (error) {
      LOG.error("Error on recalculating locations stats", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getDropLocations: async () => {
    const currentDropLocations = get()?.dropLocations;
    if (currentDropLocations?.length) {
      return;
    }
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get("locations");
      if (response?.data) {
        set({ dropLocations: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching drop locations", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getLocationMaterials: async (params = { period: "year" }) => {
    set({ isLocationMaterialsLoading: true });
    try {
      const {
        locations = [],
        type,
        places = [],
        period,
        dateValue,
        year,
        fromYear,
        toYear,
        fromMonth,
        toMonth,
        fromQuarter,
        toQuarter,
        fromWeek,
        toWeek,
      } = params;

      const requestBody: any = {
        period,
      };

      // New API: type + places (location IDs or city names)
      if (type) {
        requestBody.type = type;
        requestBody.places = places;
      } else {
        // Legacy: locations array (location IDs)
        requestBody.locations = locations;
      }

      // Period-specific fields
      if (fromYear !== undefined) requestBody.fromYear = fromYear;
      if (toYear !== undefined) requestBody.toYear = toYear;
      if (fromMonth !== undefined) requestBody.fromMonth = fromMonth;
      if (toMonth !== undefined) requestBody.toMonth = toMonth;
      if (fromQuarter !== undefined) requestBody.fromQuarter = fromQuarter;
      if (toQuarter !== undefined) requestBody.toQuarter = toQuarter;
      if (fromWeek !== undefined) requestBody.fromWeek = fromWeek;
      if (toWeek !== undefined) requestBody.toWeek = toWeek;

      // Keep old fields for backward compatibility
      if (dateValue !== undefined) requestBody.dateValue = dateValue;
      if (year !== undefined) requestBody.year = year;

      console.log("Location Materials API Request:", requestBody);

      const response = await axios.post(
        "locations/stats/materials",
        requestBody
      );
      if (response?.data) {
        set({ locationMaterials: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching location materials", { error });
    } finally {
      set({ isLocationMaterialsLoading: false });
    }
  },
  getOneDropLocation: async (id: string) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(`locations/${id}`);
      if (response?.data) {
        set({ oneDropLocation: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching one drop location", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  addDropLocation: async (dropLocation: IDropLocation) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post("locations", {
        ...dropLocation,
        type: "drop-location",
      });
      if (response?.data) {
        useAlert.getState().setAlert({
          isOpen: true,
          message: "Drop location added successfully",
          severity: "success",
        });
        if (response?.data) {
          const addedLocation = response.data;
          set((state) => ({
            dropLocations: [...(state.dropLocations ?? []), addedLocation],
            addedDropLocation: addedLocation?._id,
          }));
        }
      }
    } catch (error) {
      useAlert.getState().setAlert({
        isOpen: true,
        message: "Error on adding drop location",
        severity: "error",
      });
      LOG.error("Error on adding drop location", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  updateDropLocation: async (dropLocation: IDropLocation) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.put(
        `locations/${dropLocation?._id}`,
        dropLocation
      );
      if (response?.data) {
        const updatedLocation = response.data;
        set((state) => ({
          dropLocations: state.dropLocations?.length
            ? state.dropLocations.map((location) => {
                if (location._id === updatedLocation._id) {
                  return updatedLocation;
                }
                return location;
              })
            : state.dropLocations,
          updatedDropLocation: dropLocation?._id,
        }));
      }
    } catch (error) {
      LOG.error("Error on updating a drop location", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  /**
   * Opens or closes one store for parcel intake.
   *
   * Its own action against its own endpoint, not part of the big Location
   * form save: that payload is large and this one field decides whether a
   * store can take production registrations. Keeping them apart means a
   * validation failure elsewhere in the form cannot leave this switch in a
   * state nobody chose.
   *
   * Resolves to whether the change actually landed, so the caller can revert
   * its optimistic UI without having to read store state back.
   */
  setParcelHandoffEnabled: async (locationId: string, enabled: boolean) => {
    // One at a time: a rapid double toggle would otherwise race, and the
    // response that happens to land last would win regardless of which the
    // operator clicked last.
    if (get().isUpdatingParcelHandoffEnabled) {
      return false;
    }
    set({ isUpdatingParcelHandoffEnabled: true, parcelHandoffEnabledError: null });
    try {
      const response = await axios.patch(
        `locations/${locationId}/parcel-handoff`,
        { enabled }
      );
      const saved = Boolean(response?.data?.parcelHandoffEnabled);
      // Both caches, so the detail page and the table agree without a refetch.
      set((state) => ({
        oneDropLocation:
          state.oneDropLocation?._id === locationId
            ? { ...state.oneDropLocation, parcelHandoffEnabled: saved }
            : state.oneDropLocation,
        dropLocations:
          state.dropLocations?.map((location) =>
            location._id === locationId
              ? { ...location, parcelHandoffEnabled: saved }
              : location
          ) ?? state.dropLocations,
      }));
      return true;
    } catch (error: any) {
      LOG.error("Error on updating parcel handoff enablement", { error });
      // Surfaced, not swallowed: the switch is about to snap back and the
      // operator needs to know it was the server that refused.
      set({
        parcelHandoffEnabledError:
          error?.response?.data?.message ??
          "Could not change parcel intake for this store. Please try again.",
      });
      return false;
    } finally {
      set({ isUpdatingParcelHandoffEnabled: false });
    }
  },
  deleteDropLocation: async (id: string) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.delete(`locations/${id}`);
      if (response?.data) {
        set((state) => ({
          dropLocations: state.dropLocations?.filter((drop) => drop._id !== id),
        }));
      }
    } catch (error) {
      LOG.error("Error on deleting a drop location", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  clearAddedDropLocation: () => {
    set({ addedDropLocation: null });
  },
  clearUpdatedDropLocation: () => {
    set({ updatedDropLocation: null });
  },
}));
